import { useEffect, useMemo, useState } from 'react'
import type { BallPosition, Concept, ConceptCategory, PlayDirection, TargetingMode } from '@/types/Concept.types'
import { VALIDATION, VALIDATION_MESSAGES } from '@/constants/Validation'

export interface ConceptFormData {
	name: string
	description: string
	targetingMode: TargetingMode
	ballPosition: BallPosition
	playDirection: PlayDirection
	category: ConceptCategory
}

export interface ConceptFormErrors {
	name?: string
}

interface UseConceptFormOptions {
	concept?: Concept
	mode: 'create' | 'edit' | 'save-as'
}

/**
 * Hook for managing concept form state and validation
 * Extracts form logic from ConceptDialog component
 */
export function useConceptForm({ concept, mode }: UseConceptFormOptions) {
	const [form, setForm] = useState<ConceptFormData>({
		name: '',
		description: '',
		targetingMode: 'absolute_role',
		ballPosition: 'center',
		playDirection: 'na',
		category: 'route',
	})
	const [touched, setTouched] = useState(false)

	// Initialize form from concept when editing
	useEffect(() => {
		if (concept && mode === 'edit') {
			setForm({
				name: concept.name,
				description: concept.description ?? '',
				targetingMode: concept.targeting_mode,
				ballPosition: concept.ball_position,
				playDirection: concept.play_direction,
				category: concept.category,
			})
			setTouched(false)
		} else if (mode === 'create') {
			setForm({
				name: '',
				description: '',
				targetingMode: 'absolute_role',
				ballPosition: 'center',
				playDirection: 'na',
				category: 'route',
			})
			setTouched(false)
		}
	}, [concept, mode])

	// Validate form
	const errors = useMemo<ConceptFormErrors>(() => {
		if (!touched) return {}

		const validationErrors: ConceptFormErrors = {}
		const trimmedName = form.name.trim()

		if (trimmedName.length === 0) {
			validationErrors.name = VALIDATION_MESSAGES.CONCEPT_NAME_REQUIRED
		} else if (trimmedName.length > VALIDATION.CONCEPT_NAME_MAX_LENGTH) {
			validationErrors.name = VALIDATION_MESSAGES.CONCEPT_NAME_TOO_LONG
		}

		return validationErrors
	}, [form.name, touched])

	const isValid = useMemo(() => {
		const trimmedName = form.name.trim()
		return (
			trimmedName.length > 0 &&
			trimmedName.length <= VALIDATION.CONCEPT_NAME_MAX_LENGTH
		)
	}, [form.name])

	const updateField = <K extends keyof ConceptFormData>(
		field: K,
		value: ConceptFormData[K],
	) => {
		setForm(prev => ({ ...prev, [field]: value }))
		if (!touched) {
			setTouched(true)
		}
	}

	const updateForm = (updates: Partial<ConceptFormData>) => {
		setForm(prev => ({ ...prev, ...updates }))
		if (!touched) {
			setTouched(true)
		}
	}

	return {
		form,
		errors,
		isValid,
		touched,
		updateField,
		updateForm,
		setTouched,
	}
}
